import { useMemo, useState } from 'react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { Button } from '@/components/ui/button'
import { SegmentedControl } from '@/components/ui/segmented-control'
import type { VendorDaySeries } from '@/models/quoteAnalytics'
import { formatDay, Outcome, OUTCOMES, toCarriers, toChartRows } from './carrierDaily'
import { CarrierDailyTable } from './CarrierDailyTable'
import { CarrierDailyTooltip } from './CarrierDailyTooltip'
import { CarrierToggles } from './CarrierToggles'

/** Daily count of one outcome per carrier, with each carrier's line shown or hidden in place. */
export function CarrierDailyChart({ series }: { series: VendorDaySeries[] }) {
  const [outcome, setOutcome] = useState<Outcome>(Outcome.ERROR)
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())
  const [showTable, setShowTable] = useState(false)

  const carriers = useMemo(() => toCarriers(series), [series])
  const rows = useMemo(() => toChartRows(series, outcome), [series, outcome])
  const visible = carriers.filter((carrier) => !hidden.has(carrier.vendor))

  const toggle = (vendor: string) =>
    setHidden((prev) => {
      const next = new Set(prev)
      if (!next.delete(vendor)) next.add(vendor)
      return next
    })

  if (!carriers.length)
    return <p className="text-ink-400 py-12 text-center text-sm">No offers in this range.</p>

  return (
    <div className="flex flex-col gap-4">
      <SegmentedControl
        value={outcome}
        onValueChange={(value) => setOutcome(value as Outcome)}
        options={OUTCOMES}
        className="self-start"
      />
      <CarrierToggles carriers={carriers} hidden={hidden} onToggle={toggle} />
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#E4E3E0" vertical={false} />
          <XAxis
            dataKey="period"
            tickFormatter={(value: string) => formatDay(value)}
            tick={{ fill: '#666764', fontSize: 12 }}
            stroke="#D6D5D2"
            interval="equidistantPreserveStart"
            minTickGap={24}
          />
          <YAxis allowDecimals={false} tick={{ fill: '#666764', fontSize: 12 }} stroke="#D6D5D2" width={40} />
          <Tooltip
            content={(props) => <CarrierDailyTooltip {...props} />}
            cursor={{ stroke: '#D6D5D2', strokeWidth: 1 }}
          />
          {visible.map((carrier) => (
            <Line
              key={carrier.vendor}
              type="linear"
              dataKey={carrier.vendor}
              name={carrier.name}
              stroke={carrier.color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <Button variant="link" size="xs" className="self-start" onClick={() => setShowTable((shown) => !shown)}>
        {showTable ? 'Hide table' : 'Show table'}
      </Button>
      {showTable && <CarrierDailyTable rows={rows} carriers={visible} />}
    </div>
  )
}
